import { tracking } from '@iwoca/frontend-tracking-library';
import { Card } from '@iwoca/orion';
import { Link } from 'react-router';

import { buildQueryString } from '../../../../Buyer/utils/queryParams';
import { useAccountNavigation } from '../../../../hooks/useAccountNavigation';
import { useOnce } from '../../../../hooks/useOnce';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { getFormattedDate } from '../../../../utils/DisplayDate';
import iwocaLogo from '../../assets/iwocaLogoWhite.svg';
import { useApprovedNotTakenOffers } from '../../hooks/useApprovedNotTakenOffers';
import { SanitisedFormalOffers } from '../../utils/Dashboard.types';

const UTM_PARAMS = {
  utm_source: 'internal-website',
  utm_medium: 'iwocapay',
  utm_campaign: 'sellers_ant_card',
} as const;

export const ANTCard = () => {
  const { getAccountUrl, useCirceAccountPages } = useAccountNavigation();
  const { approvedNotTakenOffers } = useApprovedNotTakenOffers();

  useOnce({
    conditionFn: approvedNotTakenOffers?.length === 1,
    fn: () => tracking.ampli.displayedAntBanner(),
  });

  if (!approvedNotTakenOffers) return null;

  if (approvedNotTakenOffers.length === 1) {
    const [offer] = approvedNotTakenOffers;
    const dateString = getFormattedDate(offer.timeout, 'long');

    return (
      <Card className="mb-2xl !bg-ant-card text-m font-reg !text-structure-100">
        <div className="flex flex-row items-start gap-l p-s text-left">
          <img src={iwocaLogo} className="max-600:hidden" alt="" />
          <div>
            <div className="mb-s w-fit rounded-2xl bg-success-drk px-s text-s font-bold">
              Offer valid until {dateString}
            </div>
            <div className="mb-xs text-xl font-med">
              You're <b>approved</b> for a{' '}
              <b>{displayCurrency(offer.available_cash, 0)}</b> loan from iwoca.
            </div>
            <div>Hit the button to access these funds today.</div>
            {useCirceAccountPages ? (
              <Link
                to={getAccountUrl(buildQueryString(UTM_PARAMS))}
                onClick={() => tracking.ampli.clickedAntBanner()}
                className="mt-l box-border block w-fit cursor-pointer rounded-s border-none bg-ant-card-button px-l py-s text-center text-s font-bold text-primary-40 no-underline hover:bg-secondary-95 active:bg-secondary-90 max-600:w-full max-600:p-l max-600:text-m max-600:font-bold"
              >
                Access funds
              </Link>
            ) : (
              <a
                href={getAccountUrl(buildQueryString(UTM_PARAMS))}
                onClick={() => tracking.ampli.clickedAntBanner()}
                className="mt-l box-border block w-fit cursor-pointer rounded-s border-none bg-ant-card-button px-l py-s text-center text-s font-bold text-primary-40 no-underline hover:bg-secondary-95 active:bg-secondary-90 max-600:w-full max-600:p-l max-600:text-m max-600:font-bold"
              >
                Access funds
              </a>
            )}
          </div>
        </div>
      </Card>
    );
  }

  if (approvedNotTakenOffers.length > 1) {
    const timeout = getSoonestTimeout(approvedNotTakenOffers);
    const dateString = getFormattedDate(timeout, 'long');
    return (
      <Card className="mb-2xl !bg-ant-card text-m font-reg !text-structure-100">
        <div className="flex flex-row items-start gap-l p-s text-left">
          <img src={iwocaLogo} className="max-600:hidden" alt="" />
          <div>
            <div className="mb-s w-fit rounded-2xl bg-success-drk px-s text-s font-bold">
              First offer valid until {dateString}
            </div>
            <div className="mb-xs text-xl font-med">
              You've been <b>approved</b> for more than one loan with iwoca.
            </div>
            <div>
              Hit the button to see your offers and choose the one that works
              best for you.
            </div>
            {useCirceAccountPages ? (
              <Link
                to={getAccountUrl(buildQueryString())}
                className="mt-l box-border block w-fit cursor-pointer rounded-s border-none bg-ant-card-button px-l py-s text-center text-s font-bold text-primary-40 no-underline hover:bg-secondary-95 active:bg-secondary-90 max-600:w-full max-600:p-l max-600:text-m max-600:font-bold"
              >
                See your offers
              </Link>
            ) : (
              <a
                href={getAccountUrl(buildQueryString())}
                className="mt-l box-border block w-fit cursor-pointer rounded-s border-none bg-ant-card-button px-l py-s text-center text-s font-bold text-primary-40 no-underline hover:bg-secondary-95 active:bg-secondary-90 max-600:w-full max-600:p-l max-600:text-m max-600:font-bold"
              >
                See your offers
              </a>
            )}
          </div>
        </div>
      </Card>
    );
  }
  return null;
};

const getSoonestTimeout = (offers: SanitisedFormalOffers) => {
  const offerWithSoonestTimeout = offers?.reduce((prev, curr) =>
    prev.timeout < curr.timeout ? prev : curr,
  );
  return offerWithSoonestTimeout.timeout;
};
